/**
 * TrameTeacherQR.jsx — QR « Premium Moniteur » au niveau d'une PHASE.
 *
 * Différence avec les autres QR :
 *   - Réservé aux enseignants (ne s'affiche que si canSeeTeacherMode)
 *   - Ouvre la trame en mode ENSEIGNANT + vue « Débrief »
 *     → cockpit compact critères de réussite + questions pédagogiques
 *       + débrief élève, prêt à dérouler en fin de séance sur téléphone.
 *
 * URL cible : /trame?phase=<phaseId>&mode=teacher&view=debrief
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, GraduationCap, Presentation } from 'lucide-react';
import { toast } from 'sonner';

const AMBER = '#f59e0b';

function buildTeacherLink(phaseId) {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('phase', phaseId);
  url.searchParams.set('mode', 'teacher');
  url.searchParams.set('view', 'debrief');
  return url.toString();
}

export default function TrameTeacherQR({
  phaseId,
  phaseCode,
  phaseTitle,
}) {
  const link = useMemo(() => buildTeacherLink(phaseId), [phaseId]);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(`Lien moniteur ${phaseCode || phaseId} copié — garde-le sur ton téléphone.`);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Long-press sur le QR pour partager.');
    }
  };

  if (!phaseId) return null;

  return (
    <section
      className="rounded-2xl border overflow-hidden print:break-inside-avoid"
      style={{
        background: `linear-gradient(135deg, ${AMBER}12 0%, transparent 70%)`,
        borderColor: `${AMBER}55`,
      }}
      data-testid={`trame-teacher-qr-${phaseId}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
            style={{ color: AMBER, borderColor: `${AMBER}55`, background: `${AMBER}15` }}
          >
            <GraduationCap className="w-3 h-3" /> Cockpit moniteur · {phaseCode || phaseId}
          </span>
        </div>

        <div className="flex flex-col md:flex-row items-start gap-5 md:gap-6">
          {/* QR */}
          <div
            className="bg-white p-2.5 rounded-xl flex-shrink-0 mx-auto md:mx-0"
            style={{ boxShadow: `0 0 0 2px ${AMBER}66, 0 12px 30px ${AMBER}22` }}
          >
            <QRCodeSVG
              value={link}
              size={168}
              level="M"
              bgColor="#ffffff"
              fgColor="#0a0a0b"
              data-testid={`teacher-qr-svg-${phaseId}`}
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3
              className="text-white font-black text-xl sm:text-2xl leading-tight print:text-black"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Débrief express en fin de séance
            </h3>
            <p className="text-white/75 text-[13.5px] mt-2 leading-relaxed print:text-black/80">
              Réservé au <strong className="text-white print:text-black">moniteur</strong>. Ouvre en un scan le cockpit pédagogique de la séance <em>{phaseTitle}</em> : <strong>critères de réussite</strong>, <strong>questions à poser</strong> et <strong>débrief élève</strong>, prêts à dérouler.
            </p>

            <ul className="mt-3 space-y-1 text-white/70 text-[12.5px] print:text-black/70">
              <li className="flex items-start gap-1.5">
                <Presentation className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: AMBER }} />
                <span>Ouvre en mode enseignant + vue débrief</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Presentation className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: AMBER }} />
                <span>Grille synthèse : compétence / critère / questions / débrief</span>
              </li>
              <li className="flex items-start gap-1.5">
                <Presentation className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: AMBER }} />
                <span>Utilisable sur téléphone pendant le café ou en salle</span>
              </li>
            </ul>

            <div className="mt-4 flex flex-wrap items-center gap-2 print:hidden">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white/90 text-[11.5px] font-black uppercase tracking-widest transition"
                data-testid={`teacher-qr-copy-${phaseId}`}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Lien copié' : 'Copier le lien moniteur'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
